import { Box, Stack } from '@mui/material'
import React, { useCallback, useMemo, useState } from 'react'
import { setSelectedCategory } from 'store/dashboardSlice'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { COLORS } from 'styles/colors'
import { TypographyPrimary16B } from 'ui/typography/TypographyPrimary16B'
import type { DockCategoryKey } from '../dock-container/utils/widgetsByCategory'
import { useLicensedSections } from '../../hooks/useLicensedWidgets'
import { DOCK_CATEGORIES_TYPES } from '../../types/dockTypes'
import { DOCK_CATEGORIES } from './constants'

const SidebarWidgets = () => {
	const { selectedCategory } = useAppSelector(state => state.dashboardSlice)
	const licensedSections = useLicensedSections()

	const filteredCategories = useMemo(() => {
		const licensedKeys = new Set(licensedSections.map(s => s.key))
		return DOCK_CATEGORIES.filter(({ type }) =>
			type === DOCK_CATEGORIES_TYPES.ALL
				? licensedKeys.size > 0
				: licensedKeys.has(type as DockCategoryKey),
		)
	}, [licensedSections])
	const initialHoverState: Record<DOCK_CATEGORIES_TYPES, boolean> = (
		Object.values(DOCK_CATEGORIES_TYPES) as DOCK_CATEGORIES_TYPES[]
	).reduce(
		(acc, key) => {
			acc[key] = false
			return acc
		},
		{} as Record<DOCK_CATEGORIES_TYPES, boolean>,
	)

	const [hovered, setHovered] =
		useState<Record<DOCK_CATEGORIES_TYPES, boolean>>(initialHoverState)
	const dispatch = useAppDispatch()
	const handleHover = useCallback(
		(category: DOCK_CATEGORIES_TYPES, isHovered: boolean) => {
			setHovered(prev => ({ ...prev, [category]: isHovered }))
		},
		[],
	)
	const handleClick = useCallback(
		(category: DOCK_CATEGORIES_TYPES) => {
			dispatch(setSelectedCategory(category))
		},
		[dispatch],
	)

	return (
		<Stack
			direction='column'
			sx={{ width: 264, height: '100%', paddingRight: '24px' }}
			gap='8px'
		>
			{filteredCategories.map(({ label, icon, type }) => {
				const isSelected = type === selectedCategory
				const isHover = !!hovered[type]
				const iconColor = isSelected || isHover ? COLORS.main : COLORS.black
				return (
					<Box
						key={type}
						onMouseEnter={() => handleHover(type, true)}
						onMouseLeave={() => handleHover(type, false)}
						onClick={() => handleClick(type)}
						sx={{
							padding: '8px 12px',
							width: '100%',
							cursor: 'pointer',
							borderRadius: '12px',
							backgroundColor: isSelected
								? COLORS.mainSecondaryOpacity
								: 'transparent',
							userSelect: 'none',
						}}
					>
						<Stack direction='row' gap='8px' alignItems='center'>
							<Box sx={{ display: 'inline-flex' }}>
								{React.cloneElement(icon, {
									width: 16,
									height: 16,
									fill: iconColor,
									stroke: iconColor,
								})}
							</Box>
							<TypographyPrimary16B
								sx={{
									color: iconColor,
								}}
							>
								{label}
							</TypographyPrimary16B>
						</Stack>
					</Box>
				)
			})}
		</Stack>
	)
}

export default SidebarWidgets
